/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useEffect, useRef } from 'react'

import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode'
import { HeadingTagType } from '@lexical/rich-text'
import { mergeRegister } from '@lexical/utils'
import { useMemoizedFn } from 'ahooks'
import {
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_DOWN_COMMAND,
  OUTDENT_CONTENT_COMMAND
} from 'lexical'

import { useFeedback } from '@eastcoal/common/hooks/useFeedback'

import { useBlockType } from '../../hooks/useBlockType'
import { useEditor } from '../../hooks/useEditor'
import { useFontBackgroundColor } from '../../hooks/useFontBackgroundColor'
import { useFontColor } from '../../hooks/useFontColor'
import { useFontSize } from '../../hooks/useFontSize'
import { clearFormatting } from '../../utils/formatting'
import {
  CANCEL_FORMAT_PAINT_COMMAND,
  START_FORMAT_PAINT_COMMAND
} from '../FormatPainterPlugin/commands'
import { INSERT_LINK_COMMAND } from '../LinkPlugin/commands'

import {
  isCenterAlign,
  isClearFormatting,
  isDecreaseFontSize,
  isFontBackgroundColor,
  isFontColor,
  isFormatBulletList,
  isFormatCheckList,
  isFormatHeading,
  isFormatNumberedList,
  isFormatPainter,
  isFormatParagraph,
  isFormatQuote,
  isHorizontalRule,
  isIncreaseFontSize,
  isIndent,
  isInsertCodeBlock,
  isInsertLink,
  isJustifyAlign,
  isLeftAlign,
  isOutdent,
  isRightAlign,
  isStrikeThrough,
  isSubscript,
  isSuperscript
} from './shortcuts'

export const ShortcutsPlugin = () => {
  const editor = useEditor()
  const { message } = useFeedback()
  const [, setBlockType] = useBlockType()
  const { toggleFontColor } = useFontColor()
  const { toggleFontBackgroundColor } = useFontBackgroundColor()
  const { increaseFontSize, decreaseFontSize } = useFontSize()

  const escRef = useRef({
    formatPatiner: false
  })

  const keyModifierListener = useMemoizedFn((event: KeyboardEvent) => {
    if (isFormatParagraph(event)) {
      event.preventDefault()
      setBlockType({ type: 'paragraph' })
    } else if (isFormatHeading(event)) {
      event.preventDefault()
      const { code } = event
      const headingSize = `h${code[code.length - 1]}` as HeadingTagType
      setBlockType({ type: headingSize })
    } else if (isFormatBulletList(event)) {
      event.preventDefault()
      setBlockType({ type: 'bullet' })
    } else if (isFormatNumberedList(event)) {
      event.preventDefault()
      setBlockType({ type: 'number' })
    } else if (isFormatCheckList(event)) {
      event.preventDefault()
      setBlockType({ type: 'check' })
    } else if (isFormatQuote(event)) {
      event.preventDefault()
      setBlockType({ type: 'quote' })
    } else if (isHorizontalRule(event)) {
      event.preventDefault()
      editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)
    } else if (isFontColor(event)) {
      event.preventDefault()
      toggleFontColor()
    } else if (isFontBackgroundColor(event)) {
      event.preventDefault()
      toggleFontBackgroundColor()
    } else if (isStrikeThrough(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
    } else if (isIndent(event)) {
      event.preventDefault()
      editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
    } else if (isOutdent(event)) {
      event.preventDefault()
      editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
    } else if (isCenterAlign(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')
    } else if (isLeftAlign(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')
    } else if (isRightAlign(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')
    } else if (isJustifyAlign(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')
    } else if (isSubscript(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')
    } else if (isSuperscript(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')
    } else if (isInsertCodeBlock(event)) {
      event.preventDefault()
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
    } else if (isIncreaseFontSize(event)) {
      event.preventDefault()
      increaseFontSize()
    } else if (isDecreaseFontSize(event)) {
      event.preventDefault()
      decreaseFontSize()
    } else if (isClearFormatting(event)) {
      event.preventDefault()
      clearFormatting(editor)
    } else if (isFormatPainter(event)) {
      event.preventDefault()
      editor.dispatchCommand(START_FORMAT_PAINT_COMMAND, undefined)
      escRef.current.formatPatiner = true
      message.info({
        content: '已开启格式刷',
        duration: 1
      })
    } else if (isInsertLink(event)) {
      event.preventDefault()
      editor.dispatchCommand(INSERT_LINK_COMMAND, undefined)
    }
    return false
  })

  const escListener = useMemoizedFn(() => {
    if (escRef.current.formatPatiner) {
      editor.dispatchCommand(CANCEL_FORMAT_PAINT_COMMAND, undefined)
      escRef.current.formatPatiner = false
      message.info({
        content: '已关闭格式刷',
        duration: 1
      })
    }
    return false
  })

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        keyModifierListener,
        COMMAND_PRIORITY_CRITICAL
      ),

      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        escListener,
        COMMAND_PRIORITY_CRITICAL
      )
    )
  }, [editor, keyModifierListener, escListener])

  return null
}
